import React, { useState, useEffect } from "react";
import i18n from "../../i18n/config";
import { reduxForm, isValid, InjectedFormProps } from "redux-form";
import { ACTIVATE_CARD_FORM, FORM_NAMES } from "../../constants/forms";
import { ACTION_DISPLAY_TYPES, CARD_TYPES, FIELD_TYPES, ICONS } from "../../constants/enums";
import { RootState } from "../../types/redux";
import { useDispatch, useSelector } from "react-redux";
import useErrorModal from "../../hooks/useErrorModal";
import { validateCardNumber, validateConfirmCardNumber, validateConfirmPinNumber, validatePinNumber } from "../../services/customValidations";
import { ROUTES } from "../../constants/api";
import { PostEnrollmentApiData } from "../../api/general";
import { UpdateCurrentPage } from "../../actions/enrollment";
import { trackEvent } from "../../services/eventTracker";
import { EVENTS } from "../../constants/events";
import { PAGES } from "../../constants/localization";
import { EnrollmentSuccessResponse, GetEmployeeDataResponse } from "../../types/api/enrollment";
import { useLocation, useHistory } from "react-router-dom";

import DynamicInputField from "../../components/Common/DynamicFields/DynamicInputField";
import Alert from "../../components/Common/Alerts/Alert";
import Action from "../../components/Common/Action";
import ActionRow from "../../components/Layouts/ActionRow";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import ActivateCardInstructions from "../../components/ActivateCard/ActivateCardInstructions";
import EnrollmentFlowScreen from "../../components/Layouts/EnrollmentFlowScreen";

type ActivateCardProps = {
  isEnrollment?: boolean;
  currentPage?: number;
  enrollmentData?: GetEmployeeDataResponse;
};

const ActivateCard: React.FC<ActivateCardProps & InjectedFormProps<{}, ActivateCardProps>> = ({ handleSubmit, isEnrollment, currentPage, enrollmentData }) => {
  const history = useHistory();
  const [showPIN, setShowPIN] = useState(false);
  const [showConfirmPIN, setShowConfirmPIN] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const valid = useSelector((state: RootState) => isValid(FORM_NAMES.ACTIVATE_CARD)(state));
  let isInAppCard = useSelector((state: RootState) => state.card?.isInAppCard);
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const cardType = queryParams.get('type');

  if (cardType === CARD_TYPES.IN_APP) {
    isInAppCard = true;
  }

  useEffect(() => {
    // Reset the form and scroll to the top
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }, []);

  const onSubmit = async (values) => {
    setSubmitting(true);

    try {
      const { url, requestBody } = getEnrollmentUrlAndRequestBody(values);
      const response = await PostEnrollmentApiData(url, requestBody) as EnrollmentSuccessResponse;
      dispatch(UpdateCurrentPage(response.nextPage));
    } catch (err) {
      setSubmitting(false);
      trackEvent(EVENTS.ENROLLMENT.CARD_DETAILS_SUBMITTED, { success: false, failure_reason: err[0].errorMessage ?? PAGES.ENROLLMENT.ERROR_GENERAL });
      openErrorModal({
        data: { errors: err }
      });
    }
  };

  const getEnrollmentUrlAndRequestBody = (values) => {
    // Every field below comes from enrollmentData; without it the POST cannot succeed.
    // Thrown as an array because that is what the API rejects with, and what the caller's catch reads.
    if (!enrollmentData) {
      throw [{ errorMessage: PAGES.ENROLLMENT.ERROR_GENERAL }];
    }

    let url: string;
    let requestBody = {};

    // If has in-app card, use the enrollment API endpoint
    if (enrollmentData.hasInAppCard) {
      url = ROUTES.ENROLLMENT.SAVE_IN_APP_CARD_DETAILS;
      requestBody = {
        id: enrollmentData.workflowId,
        participantId: enrollmentData.participantId,
        cardId: enrollmentData.cardId,
        pin: values.PinNumber
      };
    } else {
      url = ROUTES.ENROLLMENT.SAVE_CARD_DETAILS;
      requestBody = {
        id: enrollmentData.workflowId,
        cardNumber: values.CardNumber,
        expMonth: values.expDate.month,
        expYear: values.expDate.year,
        pin: values.PinNumber,
        cardId: enrollmentData.cardId,
        bankProductId: enrollmentData.bankProductId,
        cardProductId: enrollmentData.cardProductId,
        companionParticipant: enrollmentData.companionParticipant,
        productGroupId: enrollmentData.productGroupId,
        clientId: enrollmentData.clientId,
        participantId: enrollmentData.participantId,
      }
    }

    return {
      url,
      requestBody
    }
  }

  const goBackFromCurrentPage = () => {
    // currentPage is supplied by the enrollment flow; without it there is no previous page to go to
    if (currentPage === undefined) {
      console.error("Cannot go back: currentPage was not provided to ActivateCard");
      return;
    }

    dispatch(UpdateCurrentPage(currentPage - 1));
  };

  return (
    <EnrollmentFlowScreen
      flowTitle={i18n.t("PAGES.MY_CARD.ACTIVATE_CARD.TITLE")}
      showBackButton={true}
      customBackButtonFunc={() => goBackFromCurrentPage()}
    >
      <Alert
        alertClasses={"alert-info alert-bkgrd-white spacing-top-small flex-row font-regular"}
        icon={ICONS.INFO}
        iconClasses={"spacing-right-tiny"}
      >
        <p className="bold navy-blue mb-8">{i18n.t("PAGES.ENROLLMENT.CARD_DETAILS.ALERT.HEADER")}</p>
        <p className="marengo-grey mb-0">{i18n.t("PAGES.ENROLLMENT.CARD_DETAILS.ALERT.BODY")}</p>
      </Alert>


      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <div className="max-w-360">

          {/* Don't show card number and exp date fields if in-app card */}
          {(!enrollmentData?.hasInAppCard && !isInAppCard) &&
            <>
              {isEnrollment &&
                <h3 className="navy-blue">{i18n.t("PAGES.ENROLLMENT.CARD_DETAILS.CARD_NUMBER_HEADING")}</h3>
              }
              <DynamicInputField
                label={ACTIVATE_CARD_FORM.CARD_NUMBER.LABEL}
                type={FIELD_TYPES.TEXT}
                id={ACTIVATE_CARD_FORM.CARD_NUMBER.FIELD_NAME}
                required={true}
                disabled={false}
                validate={validateCardNumber}
                max={16}
              />

              <DynamicInputField
                label={ACTIVATE_CARD_FORM.CONFIRM_CARD_NUMBER.LABEL}
                type={FIELD_TYPES.TEXT}
                id={ACTIVATE_CARD_FORM.CONFIRM_CARD_NUMBER.FIELD_NAME}
                required={true}
                disabled={false}
                validate={validateConfirmCardNumber}
                max={16}
              />

              <DynamicInputField
                label={ACTIVATE_CARD_FORM.EXPIRATION_DATE.LABEL}
                type={FIELD_TYPES.EXPIRATION_DATE}
                displayDay={false}
                id={ACTIVATE_CARD_FORM.EXPIRATION_DATE.FIELD_NAME}
                required={true}
                disabled={false}
              />
            </>
          }

          <ActivateCardInstructions isInAppCard={isInAppCard || enrollmentData?.hasInAppCard} />

          <DynamicInputField
            label={ACTIVATE_CARD_FORM.CREATE_PIN.LABEL}
            type={FIELD_TYPES.TEXT}
            displayDay={false}
            id={ACTIVATE_CARD_FORM.CREATE_PIN.FIELD_NAME}
            visibilityToggleFunc={() => setShowPIN(!showPIN)}
            visibilityToggleValue={showPIN}
            isSensitive={showPIN ? null : false}
            required={true}
            disabled={false}
            autoComplete="new-password"
            validate={validatePinNumber}
            max={4}
            className="max-w-150 form-group"
          />

          <DynamicInputField
            label={ACTIVATE_CARD_FORM.CONFIRM_PIN.LABEL}
            type={FIELD_TYPES.TEXT}
            displayDay={false}
            id={ACTIVATE_CARD_FORM.CONFIRM_PIN.FIELD_NAME}
            visibilityToggleFunc={() => setShowConfirmPIN(!showConfirmPIN)}
            visibilityToggleValue={showConfirmPIN}
            isSensitive={showConfirmPIN ? null : false}
            required={true}
            disabled={false}
            autoComplete="new-password"
            validate={validateConfirmPinNumber}
            max={4}
            className="max-w-150 form-group"
          />

        </div>

        <Alert
          alertClasses={`alert-info flex-row align-items-start w-full bg-color-white`}
          isHTML={true}
          icon={ICONS.INFO}
          iconClasses={"spacing-right-tiny"}
        >
          <p className="bold">{i18n.t("PAGES.MY_CARD.ACTIVATE_CARD.FRAUD_NOTICE.HEADING")}</p>
          <p>{i18n.t("PAGES.MY_CARD.ACTIVATE_CARD.FRAUD_NOTICE.DESCRIPTION")} <a href="tel:1-855-821-4694">1-855-821-4694</a></p>
        </Alert>

        <Action
          title={i18n.t("PAGES.ENROLLMENT.CARD_DETAILS.NEXT_BUTTON")}
          displayType={ACTION_DISPLAY_TYPES.PRIMARY}
          type="submit"
          disabled={!valid && !submitting}
          loading={submitting}
        />
      </form>
      <ApiErrorModal />
    </EnrollmentFlowScreen>
  );
}

export default reduxForm({
  form: FORM_NAMES.ACTIVATE_CARD,
  enableReinitialize: true,
})(ActivateCard);